/**
 * AFF Heartbeat popup helpers.
 *
 * The AFF service writes a synthetic "heartbeat" row (name='heart') to
 * aff_positions carrying last_connect (bumped on every successful Selkirk run)
 * and last_collect (bumped when ≥1 on-contract position was upserted).
 * This module renders those two timestamps so operators can see at a glance
 * whether the harvester is still running and still finding aircraft.
 */

export interface HeartbeatProps {
  name?: string | null;
  lastConnect?: number | string | null;
  lastCollect?: number | string | null;
  [key: string]: unknown;
}

export function isHeartbeatRow(props: HeartbeatProps): boolean {
  const name = props?.name;
  return typeof name === 'string' && name.toLowerCase().includes('heart');
}

const EM_DASH = '—';

function formatTimestamp(value: number | string | null | undefined): string {
  if (value === null || value === undefined || value === '') return EM_DASH;
  let ms: number;
  if (typeof value === 'number') {
    ms = value;
  } else {
    const numeric = Number(value);
    ms = Number.isFinite(numeric) ? numeric : Date.parse(value);
  }
  if (!Number.isFinite(ms)) return EM_DASH;
  const d = new Date(ms);
  if (Number.isNaN(d.getTime())) return EM_DASH;
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

function toEpochMs(value: number | string | null | undefined): number | null {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  const numeric = Number(value);
  if (Number.isFinite(numeric)) return numeric;
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function formatRelative(ms: number): string {
  const diff = Date.now() - ms;
  if (diff < 60_000) return 'just now';
  if (diff < 3_600_000) {
    const m = Math.floor(diff / 60_000);
    return `${m} ${m === 1 ? 'minute' : 'minutes'} ago`;
  }
  if (diff < 86_400_000) {
    const h = Math.floor(diff / 3_600_000);
    return `${h} ${h === 1 ? 'hour' : 'hours'} ago`;
  }
  const d = Math.floor(diff / 86_400_000);
  return `${d} ${d === 1 ? 'day' : 'days'} ago`;
}

export function formatHeartbeatHover(props: HeartbeatProps): string {
  const ms = toEpochMs(props.lastConnect);
  const when = ms === null ? EM_DASH : formatRelative(ms);
  return `AFF Service Heartbeat — Last connect: ${when}`;
}

export function formatHeartbeatPopup(props: HeartbeatProps): string {
  const lastConnect = escapeHtml(formatTimestamp(props.lastConnect));
  const lastCollect = escapeHtml(formatTimestamp(props.lastCollect));

  return [
    '<h3>AFF Service Heartbeat</h3>',
    `<div><strong>Last Connect:</strong> ${lastConnect}</div>`,
    `<div><strong>Last Collect:</strong> ${lastCollect}</div>`,
  ].join('');
}
